import { Button, useDisclosure } from '@chakra-ui/react';
import { useRef } from 'react';
import { useTranslations } from 'use-intl';

import {
  FundraisingDocumentType,
  SubscriptionInfosFragment,
  UserRole,
  useGetSubscriptionQuery,
  useResetSubscriptionDocumentMutation,
} from '@blockpulse3/graphql/hooks';
import { DeleteConfirmModal } from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';

type Props = {
  subscriptionId: SubscriptionInfosFragment['id'];
};

export function ResetDocumentSignature({ subscriptionId }: Props): JSX.Element | null {
  const i18nResetDocumentSignature = useTranslations('ResetDocumentSignature');

  const draftConfirmRef = useRef(null);
  const { isOpen, onClose, onOpen } = useDisclosure();

  const { user } = useUser();

  const { refetch: refetchSubscription } = useGetSubscriptionQuery({
    variables: {
      subscriptionId,
    },
  });
  const [resetDocumentSignature, { loading: isResetDocumentLoading }] =
    useResetSubscriptionDocumentMutation();

  const isUserAuthorized = user?.roles.some((role) =>
    [UserRole.COMPLIANCE_OFFICER, UserRole.SUPERADMIN].includes(role),
  );
  if (!isUserAuthorized) return null;

  const handleDocumentReset = (): void => {
    resetDocumentSignature({
      variables: {
        resetSubscriptionDocumentInput: {
          subscriptionId,
          documentType: FundraisingDocumentType.SUBSCRIPTION,
        },
      },
      onCompleted: () => {
        refetchSubscription();
        onClose();
      },
    });
  };

  return (
    <>
      <Button
        isDisabled={isResetDocumentLoading}
        isLoading={isResetDocumentLoading}
        size="sm"
        variant="secondary"
        onClick={onOpen}
      >
        {i18nResetDocumentSignature('ResetDocument')}
      </Button>
      <DeleteConfirmModal
        isOpen={isOpen}
        leastDestructiveRef={draftConfirmRef}
        subtitle={i18nResetDocumentSignature('ConfirmModal.SubTitle')}
        title={i18nResetDocumentSignature('ConfirmModal.Title')}
        onClose={onClose}
        onDelete={handleDocumentReset}
      />
    </>
  );
}
